import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import type { CatalogAdData } from "@/types/bulkAd";

interface CatalogPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  value: CatalogAdData | null;
  onChange: (data: CatalogAdData) => void;
  userId?: string;
  adAccountId?: string;
}

function buildDefaults(value: CatalogAdData | null): CatalogAdData {
  return {
    catalogId: value?.catalogId || null,
    catalogName: value?.catalogName || null,
    productSetId: value?.productSetId || null,
    productSetName: value?.productSetName || null,
    headlineType: value?.headlineType || "macro",
    headlineValue: value?.headlineValue || "{{product.name}}",
    formatType: value?.formatType || "carousel",
    primaryText: value?.primaryText || "",
    destinationUrl: value?.destinationUrl || "",
    ctaType: value?.ctaType || "SHOP_NOW",
    description: value?.description || "",
    descriptionType: value?.descriptionType || "macro",
    descriptionValue: value?.descriptionValue || "",
    automatedProductTags: value?.automatedProductTags ?? false,
  };
}

export function CatalogPopover({
  isOpen,
  onClose,
  value,
  onChange,
  userId,
  adAccountId,
}: CatalogPopoverProps) {
  const [local, setLocal] = useState<CatalogAdData>(() => buildDefaults(value));


  useEffect(() => {
    if (isOpen) {
      setLocal(buildDefaults(value));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    onChange(local);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">Catalog (DPA) Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* DPA Format */}
          <div className="space-y-1">
            <Label className="text-xs">DPA Format</Label>
            <Select
              value={local.formatType || "carousel"}
              onValueChange={(val) =>
                setLocal((prev) => ({ ...prev, formatType: val as "carousel" | "single" }))
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="carousel" className="text-xs">Carousel</SelectItem>
                <SelectItem value="single" className="text-xs">Single</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Automated Product Tags */}
          <div className="flex items-center justify-between">
            <Label className="text-xs">Automated Product Tags</Label>
            <Switch
              checked={local.automatedProductTags ?? false}
              onCheckedChange={(checked) =>
                setLocal((prev) => ({ ...prev, automatedProductTags: checked }))
              }
            />
          </div>

          <p className="text-[10px] text-muted-foreground">
            Product images come from your catalog automatically
          </p>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
